import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { pageSeoApi, propertiesApi, blogApi } from "@/lib/api";

/**
 * Fetches SEO data from the API for the current route
 * and applies it to the document head (title, meta tags, OG tags).
 *
 * Priority:
 * 1. Entity-specific SEO (property detail from properties table, blog post from blog_posts table)
 * 2. Page SEO from page_seo table
 * 3. Fallback to hardcoded index.html defaults
 */
export function useSEO() {
  const location = useLocation();

  useEffect(() => {
    const rawSlug = location.pathname.replace(/^\/+|\/+$/g, "");

    (async () => {
      try {
        // 1. Property detail page: /properties/:id
        const propertyMatch = rawSlug.match(/^properties\/(.+)$/);
        if (propertyMatch) {
          const applied = await applyPropertySEO(propertyMatch[1]);
          if (applied) return;
          await applyPageSEO("properties");
          return;
        }

        // 2. Blog detail page: /blog/:slug
        const blogMatch = rawSlug.match(/^blog\/(.+)$/);
        if (blogMatch) {
          const applied = await applyBlogSEO(blogMatch[1]);
          if (applied) return;
          await applyPageSEO("blog");
          return;
        }

        // 3. Standard page SEO
        await applyPageSEO(rawSlug || "homepage");
      } catch {
        // Silently fail — hardcoded index.html defaults remain
      }
    })();
  }, [location.pathname]);
}

/** Apply SEO from the page_seo table */
async function applyPageSEO(slug: string): Promise<boolean> {
  try {
    const res = await pageSeoApi.getBySlug(slug);
    if (!res.success || !res.data) return false;
    applySeoData(res.data.meta_title, res.data.meta_description, res.data.keywords, res.data.og_image);
    return true;
  } catch {
    return false;
  }
}

/** Apply SEO from a property's own meta fields (properties table) */
async function applyPropertySEO(idOrSlug: string): Promise<boolean> {
  try {
    const numId = parseInt(idOrSlug);
    const res = isNaN(numId)
      ? await propertiesApi.getBySlug(idOrSlug)
      : await propertiesApi.get(numId);
    if (!res.success || !res.data) return false;
    const p = res.data as any;
    const title = p.meta_title || `${p.title} | Properties`;
    const description = p.meta_description || p.short_description || p.description?.substring(0, 160) || '';
    const image = p.og_image || p.primary_image || p.image || '';
    const keywords = p.meta_keywords || p.keywords || '';
    applySeoData(title, description, keywords, image);
    return true;
  } catch {
    return false;
  }
}

/** Apply SEO from a blog post's own fields (blog_posts table) */
async function applyBlogSEO(slug: string): Promise<boolean> {
  try {
    const res = await blogApi.getBySlug(slug);
    if (!res.success || !res.data) return false;
    const post = res.data as any;
    const title = post.meta_title || `${post.title} | Blog`;
    const description = post.meta_description || post.excerpt?.substring(0, 160) || '';
    const image = post.og_image || post.image || '';
    const keywords = post.meta_keywords || post.tags || '';
    applySeoData(title, description, keywords, image);
    return true;
  } catch {
    return false;
  }
}

/** Set all SEO meta tags */
function applySeoData(title?: string, description?: string, keywords?: string, image?: string) {
  if (title) {
    document.title = title;
    setMeta("property", "og:title", title);
  }
  if (description) {
    setMeta("name", "description", description);
    setMeta("property", "og:description", description);
  }
  if (keywords) {
    setMeta("name", "keywords", keywords);
  }
  if (image) {
    setMeta("property", "og:image", image);
    setMeta("name", "twitter:image", image);
  }
}

/** Upsert a <meta> tag in <head> */
function setMeta(attr: "name" | "property", key: string, content: string) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.querySelector(selector) as HTMLMetaElement;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
}
